import { Navigate, useParams } from "react-router";
import { LoadFailure } from "@/components/shared/LoadFailure";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/Spinner";
import {
  useLatestRun,
  useRunDownload,
  useStartRun,
} from "@/hooks/use-generate";
import { refusalDetail } from "@/services/api-client";
import { subStepPath } from "@/v2/stage-vocabulary";
import { RunView } from "./RunView";

/** After this many polls in a row fail, the page says the server is out of reach. */
const UNREACHABLE_AFTER = 3;

/**
 * Your patent draft is ready: the page the run lands on, and where it shows
 * while it is still being created. A task with no run has nothing to show
 * here and goes back to the review, where a run is started.
 */
export function GenerateReadyPane() {
  const { taskId = "" } = useParams();
  const latest = useLatestRun(taskId);
  const download = useRunDownload(taskId);
  const restart = useStartRun(taskId);

  if (latest.data === undefined) {
    return latest.isError ? (
      <section className="py-10">
        <LoadFailure error={null} fallback="Your draft could not be loaded." />
        <Button
          className="mt-3"
          variant="secondary"
          size="dense"
          onClick={() => void latest.refetch()}
        >
          Try again
        </Button>
      </section>
    ) : (
      <p className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
        <Spinner size="sm" />
        Checking your draft…
      </p>
    );
  }
  const run = latest.data;
  if (run === null) {
    return <Navigate to={subStepPath(taskId, "generate", "review")} replace />;
  }
  return (
    <RunView
      run={run}
      taskId={taskId}
      unreachable={latest.failedPolls >= UNREACHABLE_AFTER}
      download={{
        onDownload: () => download.mutate(run.run_id),
        pending: download.isPending,
        failed: download.isError,
        overdue: latest.bundleOverdue,
      }}
      retry={{
        onRetry: () => restart.mutate(undefined),
        pending: restart.isPending,
        refusal: restart.isError
          ? refusalDetail(restart.error, "The draft could not be started.")
          : null,
      }}
    />
  );
}
